import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Phone, Send, Heart, Share2, MapPin, Gauge, Calendar, Fuel, Settings2, ShieldCheck, ChevronLeft, ChevronRight, Flag, Eye } from "lucide-react";
import { toast } from "sonner";
import api, { formatPrice } from "../lib/api";
import CarCard from "../components/CarCard";
import Seo from "../components/Seo";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n/I18nContext";

export default function ListingDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { t } = useI18n();
  const [listing, setListing] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [rentalOpen, setRentalOpen] = useState(false);
  const [enquiry, setEnquiry] = useState({ user_name: "", phone: "", email: "", message: "" });
  const [rental, setRental] = useState({ customer_name: "", phone: "", pickup_date: "", return_date: "", pickup_location: "", notes: "" });
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.get(`/listings/${id}`).then(({ data }) => setListing(data));
  }, [id]);

  if (!listing) return <div className="max-w-7xl mx-auto px-4 py-20 text-center text-slate-500">{t("common.loading")}</div>;

  const images = listing.images?.length ? listing.images : ["https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=940&q=80"];
  const isRental = listing.listing_type === "rental";
  const dealer = listing.dealer;
  const seoTitle = `${listing.year} ${listing.brand} ${listing.model}${listing.variant ? ` ${listing.variant}` : ''}`;
  const seoDesc = `${seoTitle} · ${isRental ? `$${listing.daily_price}/day` : `$${listing.price?.toLocaleString()}`} · ${listing.location}${listing.mileage ? ` · ${listing.mileage.toLocaleString()} km` : ''}. ${listing.description?.slice(0, 120) || ''}`;

  const submitEnquiry = async (e) => {
    e.preventDefault();
    try {
      await api.post("/enquiries", { ...enquiry, listing_id: id, enquiry_type: isRental ? "rental" : "sale" });
      toast.success(t("enq.success"));
      setEnquiryOpen(false);
      setEnquiry({ user_name: "", phone: "", email: "", message: "" });
    } catch { toast.error("Failed to send enquiry"); }
  };

  const submitRental = async (e) => {
    e.preventDefault();
    try {
      await api.post("/rental-requests", { ...rental, listing_id: id });
      toast.success(t("rent.modal.success"));
      setRentalOpen(false);
      setRental({ customer_name: "", phone: "", pickup_date: "", return_date: "", pickup_location: "", notes: "" });
    } catch { toast.error("Failed to send request"); }
  };

  const toggleSave = async () => {
    if (!user) { toast.error("Sign in to save listings"); return; }
    try {
      const { data } = await api.post(`/saved/${id}`);
      setSaved(data.saved);
      toast.success(data.saved ? "Saved" : "Removed");
    } catch (e) {
      console.error("toggleSave failed", e);
      toast.error("Could not update saved state");
    }
  };

  const phoneNumber = dealer?.phone || listing.seller?.phone || "+855120000000";
  const telegramRaw = dealer?.telegram || "";
  const telegramHref = (() => {
    const v = telegramRaw.trim();
    if (!v) return `https://t.me/+${phoneNumber.replace(/\D/g, '')}`;
    if (v.startsWith("http")) return v;
    if (v.startsWith("@")) return `https://t.me/${v.slice(1)}`;
    if (v.startsWith("+") || /^\d/.test(v)) return `https://t.me/+${v.replace(/\D/g, '')}`;
    return `https://t.me/${v}`;
  })();

  return (
    <div data-testid="listing-detail-page" className="bg-slate-50 min-h-screen pb-32 lg:pb-0">
      <Seo title={seoTitle} description={seoDesc} image={images[0]} type="product" />

      <div className="max-w-7xl mx-auto px-0 sm:px-6 lg:px-8 pt-0 sm:pt-4">
        <div className="relative bg-black aspect-[4/3] sm:aspect-[16/9] sm:rounded-2xl overflow-hidden">
          <img src={images[imgIdx]} alt={seoTitle} className="w-full h-full object-cover" />
          <div className="absolute inset-0 flex items-center justify-between px-2">
            <button data-testid="gallery-prev" onClick={() => setImgIdx((i) => (i - 1 + images.length) % images.length)} className="w-10 h-10 rounded-full glass flex items-center justify-center"><ChevronLeft className="w-5 h-5" /></button>
            <button data-testid="gallery-next" onClick={() => setImgIdx((i) => (i + 1) % images.length)} className="w-10 h-10 rounded-full glass flex items-center justify-center"><ChevronRight className="w-5 h-5" /></button>
          </div>
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
            {images.map((src, i) => (
              <button key={`dot-${src}-${i}`} onClick={() => setImgIdx(i)} className={`h-1.5 rounded-full transition-all ${i === imgIdx ? 'w-6 bg-white' : 'w-1.5 bg-white/50'}`} />
            ))}
          </div>
          <div className="absolute top-3 right-3 flex gap-2">
            <button onClick={toggleSave} data-testid="detail-save-btn" className="w-10 h-10 rounded-full glass flex items-center justify-center"><Heart className={`w-5 h-5 ${saved ? 'fill-red-500 text-red-500' : ''}`} /></button>
            <button data-testid="detail-share-btn" onClick={() => navigator.clipboard?.writeText(window.location.href).then(() => toast.success("Link copied!"))} className="w-10 h-10 rounded-full glass flex items-center justify-center"><Share2 className="w-5 h-5" /></button>
          </div>
        </div>

        <div className="px-4 sm:px-0 mt-3 flex gap-2 overflow-x-auto no-scrollbar">
          {images.map((src, i) => (
            <button key={`thumb-${src}-${i}`} onClick={() => setImgIdx(i)} className={`flex-shrink-0 w-20 h-14 rounded-lg overflow-hidden border-2 ${i === imgIdx ? 'border-cm-accent' : 'border-transparent'}`}>
              <img src={src} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>

        <div className="px-4 sm:px-0 mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl border border-slate-200 p-5 md:p-7">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <h1 data-testid="listing-title" className="font-display font-extrabold text-2xl sm:text-3xl text-cm-primary leading-tight">{seoTitle}</h1>
                </div>
                <div className="text-right">
                  {isRental ? (
                    <div>
                      <div className="font-display font-extrabold text-2xl sm:text-3xl text-cm-primary">${listing.daily_price}<span className="text-sm font-normal text-slate-500">/day</span></div>
                      <div className="text-xs text-slate-500 mt-1">{t("listing.from_per_mo")} ${listing.monthly_price}/mo</div>
                    </div>
                  ) : (
                    <div className="font-display font-extrabold text-2xl sm:text-3xl text-cm-primary">{formatPrice(listing.price)}</div>
                  )}
                </div>
              </div>

              <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-600">
                <Tag icon={MapPin}>{listing.location}</Tag>
                <Tag icon={Eye}>{listing.views || 0} {t("listing.views")}</Tag>
                {listing.featured && <span className="bg-cm-accent text-white text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full">{t("listing.featured")}</span>}
              </div>

              <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { i: Calendar, k: t("filter.year_min"), v: listing.year },
                  { i: Gauge, k: "Mileage", v: listing.mileage ? `${listing.mileage.toLocaleString()} km` : "—" },
                  { i: Settings2, k: t("filter.transmission"), v: listing.transmission || "—" },
                  { i: Fuel, k: t("filter.fuel"), v: listing.fuel_type || "—" },
                ].map((s) => (
                  <div key={s.k} className="bg-slate-50 border border-slate-200 rounded-xl p-3">
                    <s.i className="w-4 h-4 text-cm-accent" />
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-2">{s.k}</div>
                    <div className="font-semibold text-cm-primary text-sm">{s.v}</div>
                  </div>
                ))}
              </div>

              <div className="mt-6">
                <h3 className="font-display font-bold text-lg text-cm-primary">{t("listing.specs")}</h3>
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {[
                    [t("filter.body"), listing.body_type],
                    ["Engine", listing.engine_size],
                    [t("filter.condition"), listing.condition],
                    ["Colour", listing.colour],
                    ["Seller Type", listing.seller_type],
                    ...(isRental ? [
                      ["Daily price", listing.daily_price ? `$${listing.daily_price}` : null],
                      ["Weekly price", listing.weekly_price ? `$${listing.weekly_price}` : null],
                      ["Monthly price", listing.monthly_price ? `$${listing.monthly_price}` : null],
                      ["Deposit", listing.deposit ? `$${listing.deposit}` : null],
                      ["Insurance included", listing.insurance_included ? "Yes" : "No"],
                      ["Driver included", listing.driver_included ? "Yes" : "No"],
                      ["Min rental days", listing.minimum_rental_days],
                      ["Pickup location", listing.pickup_location],
                    ] : []),
                  ].filter(([, v]) => v != null && v !== "").map(([k, v]) => (
                    <div key={k} className="flex justify-between border-b border-slate-100 py-2 text-sm">
                      <span className="text-slate-500">{k}</span>
                      <span className="text-cm-primary font-medium">{v}</span>
                    </div>
                  ))}
                </div>
              </div>

              {listing.description && (
                <div className="mt-6">
                  <h3 className="font-display font-bold text-lg text-cm-primary">{t("listing.description")}</h3>
                  <p className="mt-2 text-sm text-slate-600 whitespace-pre-line leading-relaxed">{listing.description}</p>
                </div>
              )}

              <button data-testid="report-listing" className="mt-6 text-xs text-slate-500 hover:text-red-500 flex items-center gap-1.5">
                <Flag className="w-3.5 h-3.5" /> {t("listing.report")}
              </button>
            </div>

            {listing.similar?.length > 0 && (
              <div className="mt-6">
                <h3 className="font-display font-bold text-xl text-cm-primary mb-4">{t("listing.similar")}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {listing.similar.slice(0, 3).map((l) => <CarCard key={l.id} listing={l} compact />)}
                </div>
              </div>
            )}
          </div>

          <aside className="lg:col-span-1">
            <div className="bg-white rounded-2xl border border-slate-200 p-5 lg:sticky lg:top-24">
              {dealer ? (
                <Link to={`/dealer/${dealer.id}`} className="flex items-center gap-3 pb-4 border-b border-slate-100">
                  <div className="w-12 h-12 rounded-xl bg-slate-100 overflow-hidden">
                    {dealer.logo ? <img src={dealer.logo} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center font-display font-bold text-cm-primary">{dealer.dealer_name?.[0]}</div>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="font-semibold text-cm-primary truncate">{dealer.dealer_name}</span>
                      {dealer.verified_status && <ShieldCheck className="w-4 h-4 text-emerald-500 flex-shrink-0" />}
                    </div>
                    <div className="text-xs text-slate-500">★ {dealer.rating || 0} · {dealer.review_count || 0} reviews</div>
                  </div>
                </Link>
              ) : (
                <div className="pb-4 border-b border-slate-100">
                  <div className="text-xs uppercase tracking-wider text-slate-500">{t("listing.private_seller")}</div>
                  <div className="font-semibold text-cm-primary mt-1">{listing.seller?.full_name}</div>
                </div>
              )}

              <div className="mt-4 grid grid-cols-1 gap-2">
                <a href={`tel:${phoneNumber}`} data-testid="contact-call" className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-cm-primary text-white font-medium press-scale hover:bg-cm-accent transition-colors">
                  <Phone className="w-4 h-4" /> {t("listing.call")}
                </a>
                <a href={telegramHref} target="_blank" rel="noreferrer" data-testid="contact-telegram" className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-white font-medium press-scale transition-colors" style={{ backgroundColor: '#229ED9' }}>
                  <Send className="w-4 h-4" /> {t("listing.telegram")}
                </a>
                <button onClick={() => isRental ? setRentalOpen(true) : setEnquiryOpen(true)} data-testid="contact-enquire" className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white border-2 border-cm-primary text-cm-primary font-medium press-scale hover:bg-slate-50">
                  {isRental ? t("listing.req_rental") : t("listing.enquire")}
                </button>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* Sticky bottom CTA — visible on mobile + tablet for max conversion */}
      <div className="lg:hidden fixed bottom-16 left-0 right-0 z-40 bg-white/95 backdrop-blur border-t border-slate-200 shadow-[0_-4px_16px_rgba(15,23,42,0.06)] px-3 py-3 flex gap-2">
        <a href={`tel:${phoneNumber}`} data-testid="mobile-call" className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-cm-primary text-white font-semibold press-scale hover:bg-cm-accent transition-colors shadow-md">
          <Phone className="w-4 h-4" /> {t("listing.call")}
        </a>
        <a href={telegramHref} target="_blank" rel="noreferrer" data-testid="mobile-telegram" aria-label="Telegram" className="w-12 flex items-center justify-center rounded-xl text-white font-semibold press-scale" style={{ backgroundColor: '#229ED9' }}>
          <Send className="w-5 h-5" />
        </a>
        <button onClick={() => isRental ? setRentalOpen(true) : setEnquiryOpen(true)} data-testid="mobile-enquire" className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-cm-accent text-white font-semibold press-scale hover:bg-cm-primary transition-colors shadow-md">
          <Send className="w-4 h-4" /> {t("listing.enquire")}
        </button>
      </div>

      {enquiryOpen && (
        <Modal title={t("enq.title")} onClose={() => setEnquiryOpen(false)} testid="enquiry-modal">
          <form onSubmit={submitEnquiry} className="space-y-3">
            <Input testid="enq-name" label={t("enq.name")} value={enquiry.user_name} onChange={(v) => setEnquiry({ ...enquiry, user_name: v })} required />
            <Input testid="enq-phone" label={t("enq.phone")} value={enquiry.phone} onChange={(v) => setEnquiry({ ...enquiry, phone: v })} required />
            <Input testid="enq-email" label={t("enq.email")} value={enquiry.email} onChange={(v) => setEnquiry({ ...enquiry, email: v })} type="email" />
            <label className="block">
              <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1.5 font-semibold">{t("enq.message")}</span>
              <textarea data-testid="enq-message" rows={4} value={enquiry.message} onChange={(e) => setEnquiry({ ...enquiry, message: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm focus:bg-white focus:border-cm-accent outline-none" required />
            </label>
            <button data-testid="enq-submit" type="submit" className="w-full px-5 py-3 rounded-xl bg-cm-primary text-white font-medium hover:bg-cm-accent">{t("enq.submit")}</button>
          </form>
        </Modal>
      )}

      {rentalOpen && (
        <Modal title={t("rent.modal.title")} onClose={() => setRentalOpen(false)} testid="rental-modal">
          <form onSubmit={submitRental} className="space-y-3">
            <Input testid="rent-name" label={t("enq.name")} value={rental.customer_name} onChange={(v) => setRental({ ...rental, customer_name: v })} required />
            <Input testid="rent-phone" label={t("enq.phone")} value={rental.phone} onChange={(v) => setRental({ ...rental, phone: v })} required />
            <div className="grid grid-cols-2 gap-3">
              <Input testid="rent-pickup-date" label={t("rent.modal.pickup")} type="date" value={rental.pickup_date} onChange={(v) => setRental({ ...rental, pickup_date: v })} required />
              <Input testid="rent-return-date" label={t("rent.modal.return")} type="date" value={rental.return_date} onChange={(v) => setRental({ ...rental, return_date: v })} required />
            </div>
            <Input testid="rent-pickup-loc" label={t("rent.modal.pickup_loc")} value={rental.pickup_location} onChange={(v) => setRental({ ...rental, pickup_location: v })} required />
            <label className="block">
              <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1.5 font-semibold">{t("rent.modal.notes")}</span>
              <textarea data-testid="rent-notes" rows={3} value={rental.notes} onChange={(e) => setRental({ ...rental, notes: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm focus:bg-white focus:border-cm-accent outline-none" />
            </label>
            <button data-testid="rent-submit" type="submit" className="w-full px-5 py-3 rounded-xl bg-cm-primary text-white font-medium hover:bg-cm-accent">{t("rent.modal.submit")}</button>
          </form>
        </Modal>
      )}
    </div>
  );
}

function Tag({ icon: I, children }) {
  return <span className="inline-flex items-center gap-1 bg-slate-100 px-2.5 py-1 rounded-full"><I className="w-3 h-3" /> {children}</span>;
}

function Modal({ title, onClose, children, testid }) {
  return (
    <div data-testid={testid} className="fixed inset-0 z-[60] bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl p-5 max-h-[90vh] overflow-y-auto fade-in-up">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold text-lg text-cm-primary">{title}</h3>
          <button data-testid={`${testid}-close`} onClick={onClose} className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center"><span className="text-slate-600">×</span></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", required, testid }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1.5 font-semibold">{label}</span>
      <input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm focus:bg-white focus:border-cm-accent outline-none" />
    </label>
  );
}
